import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import communityService from '@/services/communityService';
import { mabarService } from '@/services/mabarService';
import badmintonService from '@/services/badmintonService';
import padelService from '@/services/padelService';
import showToast from '@/components/ui/Toast';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/Button';
import PhotoGalleryModal from '@/components/common/PhotoGalleryModal';
import { ReportOverflowButton } from '@/components/common/ReportOverflowButton';

// Helper function to format date
const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  } catch {
    return dateStr || '';
  }
};

const ClientCommunityDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [community, setCommunity] = useState<any>(null);
  const [members, setMembers] = useState<any[]>([]);
  const [memberCount, setMemberCount] = useState<number>(0);
  const [mabarEvents, setMabarEvents] = useState<any[]>([]);

  // Ringkasan Statistik Active Sport
  const [activeStatSport, setActiveStatSport] = useState<'sepakbola' | 'futsal' | 'minisoccer'>('sepakbola');

  // Riwayat Event Active Tab & Tab Cache (Lazy Loading 1x per Tab with sport_type=invasion-game & type=mabar/sparing)
  const [activeEventTab, setActiveEventTab] = useState<'mabar' | 'sparing'>('mabar');
  const [communityHostId, setCommunityHostId] = useState<number | null>(null);
  const [tabEventsCache, setTabEventsCache] = useState<{ mabar?: any[]; sparing?: any[] }>({});
  const [tabLoading, setTabLoading] = useState<boolean>(false);

  // Photo Gallery Lightbox Preview State
  const [galleryModalOpen, setGalleryModalOpen] = useState(false);
  const [galleryModalIndex, setGalleryModalIndex] = useState(0);

  const handlePhotoClick = (index: number) => {
    setGalleryModalIndex(index);
    setGalleryModalOpen(true);
  };

  useEffect(() => {
    if (!communityHostId) return;

    // Check if active tab data is already cached
    if (tabEventsCache[activeEventTab] !== undefined) {
      return;
    }

    const fetchTabEvents = async () => {
      try {
        setTabLoading(true);
        const res = await mabarService.getPublicEvents({
          host_id: communityHostId,
          sport_type: 'invasion-game',
          type: activeEventTab === 'sparing' ? 'sparing' : 'mabar',
          limit: 20,
        });

        const list = res?.data || [];
        setTabEventsCache((prev) => ({
          ...prev,
          [activeEventTab]: list,
        }));
      } catch (err) {
        console.error(`Failed to fetch ${activeEventTab} tab events:`, err);
        setTabEventsCache((prev) => ({
          ...prev,
          [activeEventTab]: [],
        }));
      } finally {
        setTabLoading(false);
      }
    };

    fetchTabEvents();
  }, [communityHostId, activeEventTab, tabEventsCache]);

  useEffect(() => {
    const fetchAllData = async () => {
      if (!slug) return;
      try {
        setLoading(true);

        // 1. Fetch Community Public Profile
        const profileRes = await communityService.getPublicProfile(slug);
        let commData = null;
        let hostId = null;

        if (profileRes?.success && profileRes?.data) {
          commData = profileRes.data.community || profileRes.data;
          hostId = profileRes.data.mabar_user_id || commData?.community_user_id;
          setCommunity(commData);
          if (hostId) setCommunityHostId(hostId);
        } else if (profileRes?.data) {
          commData = profileRes.data;
          setCommunity(commData);
          if (commData?.community_user_id) setCommunityHostId(commData.community_user_id);
        }

        // 2. Fetch Community Members
        try {
          const membersRes = await communityService.getCommunityMembers(slug, 5);
          if (membersRes?.success && membersRes?.data?.members) {
            setMembers(membersRes.data.members || []);
            if (membersRes.data.total_count) {
              setMemberCount(membersRes.data.total_count);
            }
          }
        } catch (e) {
          console.error('Failed to fetch members:', e);
        }

        // 3. Fetch Mabar Events & Game Sessions (Invasion Game Filtered)
        if (hostId) {
          try {
            const eventsRes = await mabarService.getPublicEvents({
              host_id: hostId,
              sport_type: 'invasion-game',
              limit: 20,
            });
            const rawEvents = eventsRes?.data || [];
            // Fetch game sessions for each event in parallel (supports Football, Futsal, Badminton, Padel)
            const eventsWithGames = await Promise.all(
              rawEvents.map(async (ev: any) => {
                const eventSlug = ev.slug || ev.id;
                const catSlug = (
                  ev.sport_category?.slug ||
                  ev.sport_category?.name ||
                  ev.sport_category ||
                  ev.category ||
                  ''
                ).toLowerCase();

                let gameCount = 0;
                try {
                  if (catSlug.includes('badminton')) {
                    const sessions = await badmintonService.listSessions(eventSlug);
                    gameCount = sessions.reduce(
                      (acc: number, s: any) => acc + (s.games?.length || s.gamesCount || 0),
                      0
                    );
                  } else if (catSlug.includes('padel')) {
                    const sessions = await padelService.listSessions(eventSlug);
                    gameCount = sessions.reduce(
                      (acc: number, s: any) => acc + (s.games?.length || s.gamesCount || 0),
                      0
                    );
                  } else {
                    const games = await mabarService.getGamesByEventSlug(eventSlug);
                    gameCount = games?.length || 0;
                    return {
                      ...ev,
                      gamesList: games || [],
                      gamesCount: gameCount || ev.total_games || ev.games?.length || 0,
                    };
                  }
                } catch {
                  gameCount = ev.total_games || ev.games?.length || ev.matches_count || ev.total_matches || 0;
                }

                return {
                  ...ev,
                  gamesList: ev.games || [],
                  gamesCount: gameCount || ev.total_games || ev.games?.length || 0,
                };
              })
            );

            setMabarEvents(eventsWithGames);
          } catch (e) {
            console.error('Failed to fetch mabar events:', e);
          }
        }
      } catch (err) {
        console.error('Failed to load community detail:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [slug]);

  const handleShare = () => {
    const shareUrl = `${window.location.origin}/community/${slug || community?.slug}`;
    if (navigator.share) {
      navigator.share({
        title: community?.name || 'Komunitas Sporto',
        url: shareUrl,
      });
    } else {
      navigator.clipboard.writeText(shareUrl);
      showToast.success('Link komunitas berhasil disalin!');
    }
  };

  const getInitials = (name?: string) => {
    if (!name) return 'KM';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const commName = community?.name || 'Garuda Fun Community';
  const logoUrl = community?.assets?.logo || community?.logo_url || community?.logo;
  const coverUrl = community?.assets?.cover || community?.cover_url || community?.cover;
  const initials = getInitials(commName);
  const locationName = community?.location || community?.city || community?.kota_nama || 'Yogyakarta';
  const sportsCategory =
    community?.sport_category_r?.name ||
    community?.sport_category?.name ||
    community?.sport_category ||
    community?.sportCategoryName ||
    'Sepak Bola';

  // Helper function to check if an event is Sparing
  const checkIsSparring = (event: any): boolean => {
    if (!event) return false;
    let cfg = event.config;
    if (typeof cfg === 'string') {
      try {
        cfg = JSON.parse(cfg);
      } catch {}
    }

    if (cfg?.is_sparring !== undefined && cfg?.is_sparring !== null) {
      return cfg.is_sparring === true || cfg.is_sparring === 'true';
    }
    if (cfg?.is_sparing !== undefined && cfg?.is_sparing !== null) {
      return cfg.is_sparing === true || cfg.is_sparing === 'true';
    }
    if (event.is_sparring !== undefined && event.is_sparring !== null) {
      return event.is_sparring === true || event.is_sparring === 'true';
    }
    if (event.is_sparing !== undefined && event.is_sparing !== null) {
      return event.is_sparing === true || event.is_sparing === 'true';
    }

    return event.type === 'sparing' || event.type === 'sparring' || event.event_type === 'sparing' || event.event_type === 'sparring';
  };

  // Filter events to only include Football, Futsal, and Mini Soccer
  const footballEvents = useMemo(() => {
    if (!mabarEvents) return [];
    return mabarEvents.filter((ev: any) => {
      const catSlug = (
        ev.sport_category?.slug ||
        ev.sport_category?.name ||
        ev.sport_category ||
        ev.category ||
        ''
      ).toLowerCase().replace(/[^a-z0-9]/g, '');

      return (
        catSlug.includes('futsal') ||
        catSlug.includes('soccer') ||
        catSlug.includes('mini') ||
        catSlug.includes('sepak') ||
        catSlug.includes('football')
      );
    });
  }, [mabarEvents]);

  // Filter events based on Active Event Tab (Mabar vs Sparing) for Riwayat Event
  const filteredEvents = useMemo(() => {
    if (activeEventTab === 'sparing') {
      return footballEvents.filter((ev: any) => checkIsSparring(ev));
    }
    return footballEvents.filter((ev: any) => !checkIsSparring(ev));
  }, [footballEvents, activeEventTab]);

  // Filter events by selected sport category (Invasion games: futsal, minisoccer, sepakbola)
  const eventsForActiveSport = useMemo(() => {
    if (!mabarEvents || mabarEvents.length === 0) return [];
    return mabarEvents.filter((ev: any) => {
      const catSlug = (
        ev.sport_category?.slug ||
        ev.sport_category?.name ||
        ev.sport_category ||
        ev.category ||
        ''
      ).toLowerCase().replace(/[^a-z0-9]/g, '');

      if (activeStatSport === 'futsal') {
        return catSlug.includes('futsal');
      }
      if (activeStatSport === 'minisoccer') {
        return catSlug.includes('mini');
      }
      if (activeStatSport === 'sepakbola') {
        return (catSlug.includes('sepak') || catSlug.includes('football') || (catSlug.includes('soccer') && !catSlug.includes('mini')));
      }
      return false;
    });
  }, [mabarEvents, activeStatSport]);

  const totalEventCount = eventsForActiveSport.length;

  const mabarEventCount = useMemo(() => {
    return eventsForActiveSport.filter((ev: any) => !checkIsSparring(ev)).length;
  }, [eventsForActiveSport]);

  const sparingEventsForActiveSport = useMemo(() => {
    return eventsForActiveSport.filter((ev: any) => checkIsSparring(ev));
  }, [eventsForActiveSport]);

  const sparingEventCount = sparingEventsForActiveSport.length;

  const sparingWinRate = useMemo(() => {
    let totalSparingGames = 0;
    let totalSparingWins = 0;

    sparingEventsForActiveSport.forEach((ev: any) => {
      const games = ev.gamesList || ev.games || [];
      games.forEach((game: any) => {
        totalSparingGames += 1;
        const timeline = Array.isArray(game.timeline) ? game.timeline : [];
        let homeGoals = 0;
        let awayGoals = 0;
        timeline.forEach((evt: any) => {
          if ((evt.type || '').toLowerCase() === 'goal') {
            if (evt.team === 'home') homeGoals += 1;
            else if (evt.team === 'away') awayGoals += 1;
          }
        });
        if (homeGoals > awayGoals || game.winner === 'home' || game.is_win === true) {
          totalSparingWins += 1;
        }
      });
    });

    if (totalSparingGames > 0) {
      return `${Math.round((totalSparingWins / totalSparingGames) * 100)}%`;
    }

    const backendStat =
      community?.sport_stats?.[activeStatSport] ||
      community?.information?.sport_stats?.[activeStatSport];
    if (backendStat?.win_rate !== undefined) {
      return `${Math.round(backendStat.win_rate)}%`;
    }

    return '0%';
  }, [sparingEventsForActiveSport, community, activeStatSport]);

  // Calculate total events count for Sepakbola, Futsal, and Mini Soccer (for top banner)
  const totalEvents = useMemo(() => {
    return footballEvents.length;
  }, [footballEvents]);

  // Calculate total matches dynamically from actual football/futsal/minisoccer event game sessions (for top banner)
  const totalMatches = useMemo(() => {
    if (footballEvents && footballEvents.length > 0) {
      return footballEvents.reduce((acc: number, ev: any) => {
        const count = typeof ev.gamesCount === 'number'
          ? ev.gamesCount
          : (ev.total_games || ev.games?.length || 0);
        return acc + count;
      }, 0);
    }
    return 0;
  }, [footballEvents]);



  if (loading) {
    return (
      <div className="bg-[#F8FAFC] min-h-screen flex flex-col items-center justify-center gap-3">
        <Icon icon="ph:spinner-gap-bold" className="w-8 h-8 text-[#FC480D] animate-spin" />
        <span className="text-xs font-semibold text-gray-400">Memuat detail komunitas...</span>
      </div>
    );
  }

  return (
    <div className="bg-[#F8FAFC] min-h-screen text-[#0E1726] pb-24">
      <div className="max-w-2xl mx-auto px-4 pt-12 space-y-4 animate-fade-in">
        {/* 1. HERO HEADER CARD */}
        <div className="bg-[#FC480D] rounded-3xl text-white shadow-md relative overflow-hidden">
          {/* Top Banner Cover Image Container */}
          <div className="relative w-full h-36 sm:h-44 bg-gradient-to-r from-neutral-900 via-orange-950 to-neutral-900 overflow-hidden">
            {coverUrl ? (
              <img src={coverUrl} alt="Cover Komunitas" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-[#1E293B] via-[#0F172A] to-[#E11D48] opacity-90" />
            )}
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60" />

            {/* Action Navigation Buttons */}
            <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-20">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="w-9 h-9 rounded-full bg-white text-gray-800 shadow-md hover:bg-gray-100 transition-colors flex items-center justify-center cursor-pointer"
              >
                <Icon icon="ph:arrow-left-bold" className="w-5 h-5 text-gray-800" />
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleShare}
                  className="w-9 h-9 rounded-full bg-white text-gray-800 shadow-md hover:bg-gray-100 transition-colors flex items-center justify-center cursor-pointer"
                >
                  <Icon icon="ph:share-network" className="w-5 h-5 text-gray-800" />
                </button>
                <ReportOverflowButton
                  type="Komunitas"
                  title={community?.name || slug || 'Komunitas'}
                />
              </div>
            </div>
          </div>

          {/* Overlapping Avatar & Info Container */}
          <div className="relative z-20 text-center px-5 pb-5 pt-0">
            <div className="-mt-10 mb-2 w-20 h-20 rounded-full border-4 border-[#FC480D] bg-gradient-to-br from-orange-400 to-rose-600 text-white font-extrabold text-2xl flex items-center justify-center mx-auto shadow-lg overflow-hidden shrink-0">
              {logoUrl ? (
                <img src={logoUrl} alt={commName} className="w-full h-full object-cover" />
              ) : (
                <span>{initials}</span>
              )}
            </div>

            {/* Title & Verified Checkmark */}
            <div className="flex items-center justify-center gap-1.5 pt-0.5">
              <h1 className="text-xl sm:text-2xl font-extrabold text-white tracking-tight">
                {commName}
              </h1>
              {community?.is_verified && (
                <Icon icon="ph:seal-check-fill" className="w-5 h-5 text-white shrink-0" />
              )}
            </div>


            {/* Category & Location Pills */}
            <div className="flex items-center justify-center gap-2 pt-2 flex-wrap">
              <span className="bg-white/20 backdrop-blur-xs text-white font-bold text-xs px-3 py-1 rounded-full border border-white/20">
                {sportsCategory}
              </span>
              <span className="bg-white/20 backdrop-blur-xs text-white font-bold text-xs px-3 py-1 rounded-full border border-white/20 flex items-center gap-1">
                <Icon icon="ph:map-pin-fill" className="w-3.5 h-3.5" />
                <span>{locationName}</span>
              </span>
            </div>

            {/* 3 Quick Stat Cards */}
            <div className="grid grid-cols-3 gap-2.5 pt-3">
              <div className="bg-white/15 backdrop-blur-xs rounded-2xl p-2.5 sm:p-3 text-center border border-white/20">
                <div className="flex items-center justify-center gap-1 text-white text-lg sm:text-xl font-extrabold">
                  <Icon icon="ph:calendar-blank-bold" className="w-4 h-4 text-white/90" />
                  <span>{totalEvents}</span>
                </div>
                <span className="text-[11px] font-semibold text-white/80 block mt-0.5">Event</span>
              </div>

              <div className="bg-white/15 backdrop-blur-xs rounded-2xl p-2.5 sm:p-3 text-center border border-white/20">
                <div className="flex items-center justify-center gap-1 text-white text-lg sm:text-xl font-extrabold">
                  <Icon icon="ph:soccer-ball-bold" className="w-4 h-4 text-white/90" />
                  <span>{totalMatches}</span>
                </div>
                <span className="text-[11px] font-semibold text-white/80 block mt-0.5">Pertandingan</span>
              </div>

              <div className="bg-white/15 backdrop-blur-xs rounded-2xl p-2.5 sm:p-3 text-center border border-white/20">
                <div className="flex items-center justify-center gap-1 text-white text-lg sm:text-xl font-extrabold">
                  <Icon icon="ph:users-three-bold" className="w-4 h-4 text-white/90" />
                  <span>{memberCount || community?.total_members || 0}</span>
                </div>
                <span className="text-[11px] font-semibold text-white/80 block mt-0.5">Member</span>
              </div>
            </div>
          </div>
        </div>

        {/* 2. INFO KOMUNITAS (Static Cards with Dynamic fallbacks) */}
        <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-3.5">
          <h2 className="text-base font-extrabold text-[#0E1726]">Info komunitas</h2>

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-[#F8FAFC] rounded-2xl p-3.5 border border-[#E0E6ED]/80 space-y-1">
              <div className="flex items-center gap-1.5 text-xs text-gray-400 font-medium">
                <Icon icon="ph:users-bold" className="w-4 h-4 text-[#FC480D]" />
                <span>Anggota</span>
              </div>
              <p className="text-sm font-extrabold text-[#0E1726]">
                {memberCount || community?.total_members ? `${memberCount || community?.total_members} orang` : '-'}
              </p>
            </div>

            <div className="bg-[#F8FAFC] rounded-2xl p-3.5 border border-[#E0E6ED]/80 space-y-1">
              <div className="flex items-center gap-1.5 text-xs text-gray-400 font-medium">
                <Icon icon="ph:flag-banner-bold" className="w-4 h-4 text-[#FC480D]" />
                <span>Berdiri</span>
              </div>
              <p className="text-sm font-extrabold text-[#0E1726]">
                {community?.information?.established_year || community?.established_year || (community?.created_at ? formatDate(community.created_at) : '-')}
              </p>
            </div>

            <div className="bg-[#F8FAFC] rounded-2xl p-3.5 border border-[#E0E6ED]/80 space-y-1">
              <div className="flex items-center gap-1.5 text-xs text-gray-400 font-medium">
                <Icon icon="ph:calendar-check-bold" className="w-4 h-4 text-[#FC480D]" />
                <span>Jadwal rutin</span>
              </div>
              <p className="text-sm font-extrabold text-[#0E1726]">
                {community?.information?.schedule || community?.schedule || '-'}
              </p>
            </div>

            <div className="bg-[#F8FAFC] rounded-2xl p-3.5 border border-[#E0E6ED]/80 space-y-1">
              <div className="flex items-center gap-1.5 text-xs text-gray-400 font-medium">
                <Icon icon="ph:map-pin-line-bold" className="w-4 h-4 text-[#FC480D]" />
                <span>Home venue</span>
              </div>
              <p className="text-sm font-extrabold text-[#0E1726] truncate">
                {community?.information?.home_venue || community?.home_venue || community?.address || '-'}
              </p>
            </div>
          </div>
        </div>

        {/* 3. ANGGOTA SECTION */}
        <Card className="p-5 space-y-3.5">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-extrabold text-[#0E1726]">Anggota</h2>
          </div>

          <div className="space-y-2.5">
            {members.length > 0 ? (
              members.slice(0, 5).map((m: any, idx: number) => {
                const name =
                  m.community_user?.name ||
                  m.community_user?.nickname ||
                  m.user?.name ||
                  m.name ||
                  `Anggota ${idx + 1}`;
                const avatar =
                  m.community_user?.avatar ||
                  m.user?.avatar_url ||
                  m.avatar_url ||
                  m.avatar;
                const position = m.position || m.role || 'Member';
                const joinDate = m.join_date || m.created_at ? formatDate(m.join_date || m.created_at) : '-';
                const memberInitials = getInitials(name);
                const bgColors = ['bg-orange-500', 'bg-blue-500', 'bg-emerald-500', 'bg-purple-500', 'bg-rose-500'];
                const bg = bgColors[idx % bgColors.length];
                const username =
                  m.community_user?.username ||
                  m.user?.username ||
                  m.username ||
                  m.user_slug ||
                  m.slug;

                return (
                  <div
                    key={m.id || idx}
                    onClick={() => {
                      if (username) {
                        navigate(`/profile/${username}`);
                      }
                    }}
                    className="bg-white rounded-2xl p-3 border border-[#E0E6ED] flex items-center justify-between gap-3 shadow-2xs hover:border-[#FC480D]/40 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {avatar ? (
                        <img src={avatar} alt={name} className="w-10 h-10 rounded-full object-cover shrink-0 border border-gray-100" />
                      ) : (
                        <div className={`w-10 h-10 rounded-full ${bg} text-white font-bold text-xs flex items-center justify-center shrink-0 shadow-2xs`}>
                          {memberInitials}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <h4 className="text-xs font-extrabold text-[#0E1726] truncate">{name}</h4>
                          {(position === 'host' || position === 'admin') && (
                            <Icon icon="ph:seal-check-fill" className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                          )}
                        </div>
                        <p className="text-[11px] text-gray-400 font-medium flex items-center gap-1 mt-0.5">
                          <Icon icon="ph:calendar-blank-bold" className="w-3 h-3 text-gray-400" />
                          <span>Gabung {joinDate}</span>
                        </p>
                      </div>
                    </div>
                    <Badge
                      className={`text-[11px] font-bold px-3 py-1 rounded-full shrink-0 border capitalize ${
                        position === 'host'
                          ? 'bg-amber-50 text-amber-700 border-amber-200'
                          : position === 'admin'
                          ? 'bg-blue-50 text-blue-700 border-blue-200'
                          : 'bg-amber-50/60 text-amber-800 border-amber-200/60'
                      }`}
                    >
                      {position}
                    </Badge>
                  </div>
                );
              })
            ) : (
              <div className="bg-[#F8FAFC] rounded-2xl p-5 text-center border border-dashed border-gray-200">
                <p className="text-xs text-gray-400 font-medium">Belum ada anggota komunitas</p>
              </div>
            )}
          </div>

          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate(`/community/${slug}/members`)}
            className="w-full h-11 rounded-2xl font-bold text-xs flex items-center justify-center gap-2"
          >
            <Icon icon="ph:users-three-bold" className="w-4 h-4 mr-3" />
            <span>Lihat semua anggota</span>
          </Button>
        </Card>

        {/* 4. RINGKASAN STATISTIK */}
        <Card className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-bold text-[#0E1726]">Ringkasan statistik</h3>
          </div>

          {/* Sport Selector Pills (Order: Futsal | Mini soccer | Sepak bola) */}
          <div className="bg-[#F5F6F7] p-1 rounded-2xl flex items-center justify-between gap-1 text-xs font-semibold text-gray-500">
            <button
              type="button"
              onClick={() => setActiveStatSport('futsal')}
              className={`flex-1 py-2 rounded-xl transition-all cursor-pointer ${
                activeStatSport === 'futsal'
                  ? 'bg-white text-[#FC480D] shadow-2xs font-bold'
                  : 'hover:text-[#0E1726]'
              }`}
            >
              Futsal
            </button>
            <button
              type="button"
              onClick={() => setActiveStatSport('minisoccer')}
              className={`flex-1 py-2 rounded-xl transition-all cursor-pointer ${
                activeStatSport === 'minisoccer'
                  ? 'bg-white text-[#FC480D] shadow-2xs font-bold'
                  : 'hover:text-[#0E1726]'
              }`}
            >
              Mini soccer
            </button>
            <button
              type="button"
              onClick={() => setActiveStatSport('sepakbola')}
              className={`flex-1 py-2 rounded-xl transition-all cursor-pointer ${
                activeStatSport === 'sepakbola'
                  ? 'bg-white text-[#FC480D] shadow-2xs font-bold'
                  : 'hover:text-[#0E1726]'
              }`}
            >
              Sepak bola
            </button>
          </div>

          {/* Stat Grid 2x2 (4 Cards) */}
          <div className="grid grid-cols-2 gap-3">
            {/* Total Event */}
            <div className="bg-white border border-[#E0E6ED] rounded-2xl p-3.5 flex items-center gap-3 shadow-2xs">
              <div className="w-9 h-9 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center shrink-0">
                <Icon icon="ph:calendar-blank-bold" className="w-5 h-5" />
              </div>
              <div>
                <span className="text-xl font-bold text-[#0E1726] block">
                  {totalEventCount}
                </span>
                <span className="text-xs text-gray-400 font-medium">Total event</span>
              </div>
            </div>

            {/* Event Mabar */}
            <div className="bg-white border border-[#E0E6ED] rounded-2xl p-3.5 flex items-center gap-3 shadow-2xs">
              <div className="w-9 h-9 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center shrink-0">
                <Icon icon="ph:users-three-bold" className="w-5 h-5" />
              </div>
              <div>
                <span className="text-xl font-bold text-[#0E1726] block">
                  {mabarEventCount}
                </span>
                <span className="text-xs text-gray-400 font-medium">Event mabar</span>
              </div>
            </div>

            {/* Total Sparing */}
            <div className="bg-white border border-[#E0E6ED] rounded-2xl p-3.5 flex items-center gap-3 shadow-2xs">
              <div className="w-9 h-9 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center shrink-0">
                <Icon icon="ph:shield-bold" className="w-5 h-5" />
              </div>
              <div>
                <span className="text-xl font-bold text-[#0E1726] block">
                  {sparingEventCount}
                </span>
                <span className="text-xs text-gray-400 font-medium">Total sparing</span>
              </div>
            </div>

            {/* Winrate Sparing */}
            <div className="bg-white border border-[#E0E6ED] rounded-2xl p-3.5 flex items-center gap-3 shadow-2xs">
              <div className="w-9 h-9 rounded-xl bg-orange-50 text-[#FC480D] flex items-center justify-center shrink-0">
                <Icon icon="ph:trophy-bold" className="w-5 h-5" />
              </div>
              <div>
                <span className="text-xl font-bold text-[#0E1726] block">
                  {sparingWinRate}
                </span>
                <span className="text-xs text-gray-400 font-medium">Winrate sparing</span>
              </div>
            </div>
          </div>
        </Card>

        {/* 5. RIWAYAT EVENT */}
        <Card className="p-5 space-y-3.5">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-extrabold text-[#0E1726]">Riwayat event</h2>
          </div>

          {/* Filter Pills */}
          <div className="bg-[#F5F6F7] p-1 rounded-2xl flex items-center justify-between gap-1 text-xs font-semibold text-gray-500">
            <button
              type="button"
              onClick={() => setActiveEventTab('mabar')}
              className={`flex-1 py-2 rounded-xl transition-all cursor-pointer ${
                activeEventTab === 'mabar'
                  ? 'bg-white text-[#FC480D] shadow-2xs font-bold'
                  : 'hover:text-[#0E1726]'
              }`}
            >
              Mabar
            </button>
            <button
              type="button"
              onClick={() => setActiveEventTab('sparing')}
              className={`flex-1 py-2 rounded-xl transition-all cursor-pointer ${
                activeEventTab === 'sparing'
                  ? 'bg-white text-[#FC480D] shadow-2xs font-bold'
                  : 'hover:text-[#0E1726]'
              }`}
            >
              Sparing
            </button>
          </div>

          {/* Event Items */}
          <div className="space-y-2.5 pt-1">
            {(tabLoading || (loading && tabEventsCache[activeEventTab] === undefined)) ? (
              <div className="space-y-2.5">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="p-3 border border-[#E0E6ED] rounded-2xl flex items-center justify-between gap-3 animate-pulse">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-xl bg-gray-200 shrink-0" />
                      <div className="space-y-1.5 flex-1 min-w-0">
                        <div className="h-4 w-32 bg-gray-200 rounded" />
                        <div className="h-3 w-44 bg-gray-200 rounded" />
                      </div>
                    </div>
                    <div className="h-8 w-16 rounded-full bg-gray-200 shrink-0" />
                  </div>
                ))}
              </div>
            ) : (tabEventsCache[activeEventTab] ?? filteredEvents).length === 0 ? (
              <div className="py-6 text-center text-xs text-gray-400 font-medium border border-dashed border-gray-200 rounded-2xl">
                Belum ada event {activeEventTab === 'sparing' ? 'sparing' : 'mabar'} untuk komunitas ini.
              </div>
            ) : (
              (tabEventsCache[activeEventTab] ?? filteredEvents).slice(0, 5).map((event: any, idx: number) => {
                const dateStr = event.event_date || event.date
                  ? formatDate(event.event_date || event.date)
                  : '';
                const locationStr = event.lokasi_arena || event.location || event.city || '';
                const subtitle = [dateStr, locationStr].filter(Boolean).join(' · ');
                const isEventSparring = checkIsSparring(event);
                const badgeText = event.badge || (isEventSparring ? 'SP' : 'MB');
                const badgeBg = event.badge_bg || (isEventSparring ? 'bg-indigo-600 text-white' : 'bg-orange-500 text-white');

                const rawDate = event.event_date || event.date;
                const isUpcoming = rawDate ? new Date(rawDate).getTime() >= new Date().setHours(0, 0, 0, 0) : false;
                const isFinished = event.status === 'completed' || event.status === 'finished' || (!isUpcoming && rawDate);

                const eventLogo = event.host_avatar || event.host?.avatar || event.host_logo || logoUrl;

                return (
                  <div
                    key={event.slug || event.id || idx}
                    onClick={() => navigate(`/event/${event.slug}`)}
                    className="p-3 border border-[#E0E6ED] rounded-2xl flex items-center justify-between gap-3 shadow-2xs hover:bg-gray-50/50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      {eventLogo ? (
                        <img
                          src={eventLogo}
                          alt={event.name || event.title}
                          className="w-10 h-10 rounded-2xl object-cover shrink-0 border border-gray-100 shadow-2xs"
                        />
                      ) : (
                        <div className={`w-10 h-10 rounded-2xl ${badgeBg} font-extrabold text-xs flex items-center justify-center shrink-0`}>
                          {badgeText}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-bold text-[#0E1726] truncate">{event.name || event.title}</h4>
                          {isUpcoming ? (
                            <Badge variant="emerald" className="py-0.5 px-2 text-[10px] font-bold shrink-0">
                              Mendatang
                            </Badge>
                          ) : isFinished ? (
                            <Badge variant="outline" className="py-0.5 px-2 text-[10px] font-medium text-gray-500 shrink-0">
                              Selesai
                            </Badge>
                          ) : null}
                        </div>
                        <p className="text-xs text-gray-400 font-medium truncate mt-0.5">{subtitle || 'Detail lokasi & tanggal'}</p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/event/${event.slug}`);
                      }}
                      className="rounded-full px-3 py-1.5 h-auto text-xs font-bold gap-1 shrink-0"
                    >
                      <Icon icon="ph:caret-right-bold" className="w-3.5 h-3.5" />
                      <span>Detail</span>
                    </Button>
                  </div>
                );
              })
            )}
          </div>

          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate(`/community/${slug}/events`)}
            className="w-full h-11 rounded-2xl font-bold text-xs flex items-center justify-center gap-2"
          >
            <Icon icon="ph:calendar-blank-bold" className="w-4 h-4 mr-3" />
            <span>Lihat semua event</span>
          </Button>
        </Card>

        {/* 6. GALERI */}
        <Card className="p-5 space-y-3.5">
          <h2 className="text-base font-extrabold text-[#0E1726]">Galeri</h2>

          {community?.assets?.gallery && community.assets.gallery.length > 0 ? (
            <div className="grid grid-cols-3 gap-2.5">
              {community.assets.gallery.map((imgUrl: string, idx: number) => (
                <div
                  key={idx}
                  onClick={() => handlePhotoClick(idx)}
                  className="aspect-square rounded-2xl overflow-hidden bg-gray-100 border border-gray-100 hover:opacity-90 transition-opacity cursor-pointer"
                >
                  <img
                    src={imgUrl}
                    alt={`Galeri ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-[#F8FAFC] rounded-2xl p-6 border border-dashed border-gray-200 text-center space-y-1.5">
              <Icon icon="ph:image-square-bold" className="w-8 h-8 text-gray-300 mx-auto" />
              <p className="text-xs font-bold text-gray-500">Belum ada foto galeri</p>
              <p className="text-[11px] text-gray-400 font-medium">
                Komunitas ini belum mengunggah foto kegiatan.
              </p>
            </div>
          )}
        </Card>
      </div>

      <PhotoGalleryModal
        open={galleryModalOpen}
        onOpenChange={setGalleryModalOpen}
        photos={community?.assets?.gallery || []}
        initialIndex={galleryModalIndex}
        title={`Galeri ${community?.name || 'Komunitas'}`}
      />
    </div>
  );
};

export default ClientCommunityDetailPage;
